import React, { useState } from 'react';
import { 
  Bell, BellOff, Brain, ChevronRight, AlertCircle, Clock, 
  AlertTriangle, ThumbsUp, CalendarPlus, UserPlus, Mail, Activity
} from 'lucide-react';
import { DESIGN } from '../DesignSystem';

// ================================================
// ALERT CARD - Individual Alert Item
// ================================================
export const AlertCard = ({ alert, onAction }) => {
  const severityStyles = {
    critical: { bg: DESIGN.colors.errorSubtle, border: 'rgba(239, 68, 68, 0.3)', icon: AlertCircle, color: DESIGN.colors.error },
    urgent: { bg: DESIGN.colors.amberSubtle, border: 'rgba(245, 158, 11, 0.3)', icon: Clock, color: '#D97706' },
    warning: { bg: DESIGN.colors.amberSubtle, border: 'rgba(245, 158, 11, 0.3)', icon: AlertTriangle, color: '#D97706' },
    info: { bg: DESIGN.colors.blueSubtle, border: 'rgba(59, 130, 246, 0.3)', icon: Bell, color: '#4F46E5' },
    positive: { bg: DESIGN.colors.successSubtle, border: 'rgba(16, 185, 129, 0.3)', icon: ThumbsUp, color: DESIGN.colors.success }
  };
  
  const style = severityStyles[alert.severity] || severityStyles.info;
  const Icon = style.icon;

  return (
    <div 
      className="p-4 rounded-xl border transition-all hover:shadow-md cursor-pointer group"
      style={{ 
        backgroundColor: style.bg, 
        borderColor: style.border,
        boxShadow: `inset 0 0 0 1px ${style.border}`
      }}
      onClick={() => onAction(alert)}
      data-testid={`alert-${alert.id}`}
    >
      <div className="flex items-start gap-3">
        <div 
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${style.color}15` }}
        >
          <Icon className="w-4 h-4" style={{ color: style.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>{alert.title}</p>
          <p className="text-xs mt-1" style={{ color: DESIGN.colors.textSecondary }}>{alert.description}</p>
        </div>
        <button 
          className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
          style={{ 
            backgroundColor: `${style.color}20`,
            color: style.color
          }}
        >
          {alert.action_label}
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// ================================================
// INTELLIGENCE PANEL - Alerts & Priorities Tab Panel
// ================================================
export const IntelligencePanel = ({ alerts, recommendations, onAlertAction, onRecommendationAction }) => {
  const [activeTab, setActiveTab] = useState('alerts');

  return (
    <div 
      className="rounded-2xl overflow-hidden transition-all hover:shadow-lg"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        boxShadow: DESIGN.shadows.lg
      }}
      data-testid="intelligence-panel"
    >
      {/* Tab header */}
      <div 
        className="flex border-b"
        style={{ borderColor: DESIGN.colors.border }}
      >
        {[
          { key: 'alerts', label: 'Alerts', icon: Bell, count: alerts.length },
          { key: 'priorities', label: 'Priorities', icon: Brain, count: null }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="flex-1 px-4 py-4 text-sm font-semibold transition-all"
            style={{ 
              backgroundColor: activeTab === tab.key ? DESIGN.colors.primarySubtle : 'transparent',
              color: activeTab === tab.key ? DESIGN.colors.primary : DESIGN.colors.textMuted,
              borderBottom: activeTab === tab.key ? `2px solid ${DESIGN.colors.primary}` : '2px solid transparent'
            }}
          >
            <div className="flex items-center justify-center gap-2">
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== null && tab.count > 0 && (
                <span 
                  className="w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-bold"
                  style={{ backgroundColor: DESIGN.colors.error }}
                >
                  {tab.count}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-5">
        {activeTab === 'alerts' ? (
          <div className="space-y-3">
            {alerts.length === 0 ? (
              <div className="text-center py-8">
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3"
                  style={{ backgroundColor: DESIGN.colors.background }}
                >
                  <BellOff className="w-6 h-6" style={{ color: DESIGN.colors.textMuted }} />
                </div>
                <p className="text-sm font-medium" style={{ color: DESIGN.colors.textMuted }}>No active alerts</p>
              </div>
            ) : (
              alerts.map(alert => (
                <AlertCard key={alert.id} alert={alert} onAction={onAlertAction} />
              ))
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {recommendations.map((rec, idx) => (
              <div 
                key={rec.id}
                className="p-4 rounded-xl border hover:shadow-md transition-all cursor-pointer group"
                style={{ 
                  borderColor: DESIGN.colors.border,
                  backgroundColor: idx === 0 ? DESIGN.colors.primarySubtle : 'transparent'
                }}
                onClick={() => onRecommendationAction(rec)}
                data-testid={`recommendation-${rec.id}`}
              >
                <div className="flex items-start gap-3">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black text-white flex-shrink-0"
                    style={{ 
                      backgroundColor: rec.priority === 'critical' ? DESIGN.colors.error 
                        : rec.priority === 'high' ? DESIGN.colors.amber 
                        : rec.priority === 'medium' ? '#3B82F6' 
                        : DESIGN.colors.textMuted 
                    }}
                  >
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>{rec.title}</p>
                    <p className="text-xs mt-1" style={{ color: DESIGN.colors.textSecondary }}>{rec.description}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <span 
                        className="text-xs font-semibold px-3 py-1 rounded-full"
                        style={{ backgroundColor: DESIGN.colors.blueSubtle, color: '#4F46E5' }}
                      >
                        Impact: {rec.impact_score}%
                      </span>
                      {rec.estimated_time_minutes && (
                        <span className="text-xs font-medium" style={{ color: DESIGN.colors.textMuted }}>
                          ~{rec.estimated_time_minutes} min
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: DESIGN.colors.textMuted }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ================================================
// QUICK ACTIONS PANEL - Action Buttons Grid
// ================================================
export const QuickActionsPanel = ({ onAction, customActions, isUniqueIP }) => {
  const defaultActions = [
    { key: 'schedule', label: 'Schedule Meeting', icon: CalendarPlus, color: '#6366F1' },
    { key: 'delegate', label: 'Delegate Task', icon: UserPlus, color: DESIGN.colors.primary },
    { key: 'message', label: 'Send Message', icon: Mail, color: '#EC4899' },
    { key: 'insights', label: 'View Insights', icon: Activity, color: DESIGN.colors.amber, path: '/insights' }
  ];
  
  // Icon mapping for custom actions
  const CUSTOM_ICON_MAP = {
    Calendar: CalendarPlus,
    TrendingUp: Activity,
    Target: Activity,
    AlertTriangle: AlertTriangle,
    CheckCircle2: ThumbsUp,
    MessageSquare: Mail,
    Users: UserPlus,
    Sparkles: Brain,
    Clock: Clock
  };
  
  const accentColor = isUniqueIP ? '#F59E0B' : DESIGN.colors.primary;
  
  // Use custom actions if provided, otherwise default
  const actions = customActions 
    ? customActions.map(a => ({
        key: a.key,
        label: a.label,
        icon: CUSTOM_ICON_MAP[a.icon] || Activity,
        color: isUniqueIP ? '#F59E0B' : (a.color || DESIGN.colors.primary)
      }))
    : defaultActions;

  return (
    <div 
      className="rounded-2xl p-6 transition-all hover:shadow-lg"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        boxShadow: DESIGN.shadows.lg
      }}
      data-testid="quick-actions-panel"
    >
      <h3 className="text-base font-bold mb-4" style={{ color: DESIGN.colors.textPrimary }}>Quick Actions</h3>
      <div className="grid grid-cols-2 gap-3">
        {actions.map(action => (
          <button 
            key={action.key}
            onClick={() => onAction(action.key, action.path)}
            className="flex items-center gap-3 p-4 rounded-xl transition-all text-left group hover:shadow-md"
            style={{ backgroundColor: DESIGN.colors.background }}
            data-testid={`quick-action-${action.key}`}
          >
            <div 
              className="w-11 h-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
              style={{ 
                backgroundColor: `${action.color}15`,
                boxShadow: `0 0 15px ${action.color}20`
              }}
            >
              <action.icon className="w-5 h-5" style={{ color: action.color }} />
            </div>
            <span className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>{action.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// ================================================
// QUICK TEAM PANEL - Team Overview
// ================================================
export const QuickTeamPanel = ({ team, onMemberClick, title, isUniqueIP }) => {
  const accentColor = isUniqueIP ? '#F59E0B' : DESIGN.colors.primary;
  const accentDark = isUniqueIP ? '#D97706' : DESIGN.colors.primaryDark;
  const accentGlow = isUniqueIP ? '0 0 15px rgba(245, 158, 11, 0.25)' : DESIGN.shadows.glowPrimary;
  
  const getAvailabilityColor = (status) => {
    switch (status) {
      case 'available': return '#10B981';
      case 'busy': return DESIGN.colors.amber;
      case 'away': return DESIGN.colors.textMuted;
      case 'do_not_disturb': return DESIGN.colors.error;
      default: return DESIGN.colors.textMuted;
    }
  };

  return (
    <div 
      className="rounded-2xl p-6 transition-all hover:shadow-lg"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        boxShadow: DESIGN.shadows.lg
      }}
      data-testid="quick-team-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>{title || 'Key Team'}</h3>
        <button className="text-xs font-semibold hover:underline" style={{ color: accentColor }}>
          View All
        </button>
      </div>
      <div className="space-y-2">
        {team.map(member => (
          <button
            key={member.id}
            onClick={() => onMemberClick(member)}
            className="w-full flex items-center gap-4 p-3 rounded-xl transition-all text-left group hover:shadow-md"
            style={{ backgroundColor: DESIGN.colors.background }}
          >
            <div className="relative">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold text-white"
                style={{ 
                  background: `linear-gradient(135deg, ${accentColor} 0%, ${accentDark} 100%)`,
                  boxShadow: accentGlow
                }}
              >
                {member.avatar}
              </div>
              <span 
                className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-white"
                style={{ backgroundColor: getAvailabilityColor(member.availability_status) }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: DESIGN.colors.textPrimary }}>{member.name}</p>
              <p className="text-xs truncate" style={{ color: DESIGN.colors.textMuted }}>{member.title || 'Team Member'}</p>
            </div>
            <div className="text-right">
              <p className="text-xs font-medium" style={{ color: DESIGN.colors.textSecondary }}>{member.active_tasks} tasks</p>
              <div 
                className="w-14 h-2 rounded-full mt-1.5"
                style={{ backgroundColor: '#E5E7EB' }}
              >
                <div 
                  className="h-full rounded-full transition-all"
                  style={{ 
                    width: `${Math.min(100, member.cognitive_load || 0)}%`,
                    backgroundColor: (member.cognitive_load || 0) > 70 ? DESIGN.colors.error 
                      : (member.cognitive_load || 0) > 50 ? DESIGN.colors.amber 
                      : '#10B981'
                  }}
                />
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default { AlertCard, IntelligencePanel, QuickActionsPanel, QuickTeamPanel };
